import { useCallback, useEffect, useMemo, useState } from 'react'

import { Box } from '@factorialco/deprecated-design-system'
import { F0Alert, F0Dialog } from '@factorialco/f0-react'
import { OneDataCollection, useDataCollectionSource } from '@factorialco/f0-react/dist/experimental'
import {
  BulkPaymentDetailPageDocument,
  BulkPaymentRejectedTransfersDocument,
  BulkPaymentTransferStatsDocument,
} from 'generated/resources/graphql'

import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'
import MoneyWithCurrency from 'modules/core/lib/money'

import { useRetryBankingPayment } from 'modules/banking/hooks/useRetryBankingPayment'

interface RejectedTransfer {
  id: string
  beneficiaryName: string | null
  accountNumber: string | null
  amountCents: number
  currency: string
  rejectionReason: string | null
  paymentRequestLabel: string | null
  [key: string]: unknown
}

// Map backend rejection reason to translated text
const translateRejectionReason = (reason: string | null): string => {
  if (!reason) return '-'

  const translations: Record<string, string> = {
    card_error: i18n.t('banking.transfers.rejectionReasons.cardError'),
    creditor_error: i18n.t('banking.transfers.rejectionReasons.creditorError'),
    debtor_error: i18n.t('banking.transfers.rejectionReasons.debtorError'),
    factorial_error: i18n.t('banking.transfers.rejectionReasons.factorialError'),
    provider_error: i18n.t('banking.transfers.rejectionReasons.providerError'),
    rejected_consent: i18n.t('banking.transfers.rejectionReasons.rejectedConsent'),
    technical_error: i18n.t('banking.transfers.rejectionReasons.technicalError'),
    transfer_error: i18n.t('banking.transfers.rejectionReasons.transferError'),
    unknown_error: i18n.t('banking.transfers.rejectionReasons.unknownError'),
  }

  return translations[reason] ?? reason.replace(/_/g, ' ')
}

interface Props {
  paymentId: string
  payerId: string
  paymentExecutionIds: string[]
  locale: string
  onClose: () => void
  onSuccess: () => void
}

export const ConfirmRetryBulkPaymentModal = ({
  paymentId,
  payerId,
  paymentExecutionIds,
  locale,
  onClose,
  onSuccess,
}: Props) => {
  const { data, loading: loadingTransfers } = useQuery(BulkPaymentRejectedTransfersDocument, {
    variables: { paymentExecutionIds },
  })

  const { retryPayment, loading: loadingRetry } = useRetryBankingPayment({
    refetchQueries: [BulkPaymentDetailPageDocument, BulkPaymentTransferStatsDocument],
  })

  const rejectedTransfers = useMemo<RejectedTransfer[]>(() => {
    if (!data?.banking?.transfersConnection?.edges) return []
    return data.banking.transfersConnection.edges.map((edge) => ({
      id: edge.node.id,
      beneficiaryName: edge.node.beneficiaryName,
      accountNumber: edge.node.accountNumber,
      amountCents: edge.node.amountCents,
      currency: edge.node.currency,
      rejectionReason: edge.node.rejectionReason ?? null,
      paymentRequestLabel: edge.node.paymentRequestItem?.paymentRequest?.label ?? null,
    }))
  }, [data])

  // Track selection - initialize with all selected when data loads
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [initialized, setInitialized] = useState(false)

  // Initialize selection when data loads
  useEffect(() => {
    if (rejectedTransfers.length > 0 && !initialized) {
      setSelectedIds(new Set(rejectedTransfers.map((t) => t.id)))
      setInitialized(true)
    }
  }, [rejectedTransfers, initialized])

  const handleSelectItems = useCallback(
    (selection: {
      allSelected: boolean | 'indeterminate'
      selectedIds: ReadonlyArray<string | number>
      selectedCount: number
    }) => {
      if (selection.allSelected === true) {
        // All selected means all transfers
        setSelectedIds(new Set(rejectedTransfers.map((t) => t.id)))
      } else {
        // Use selectedIds to track what's selected
        const selected = new Set<string>()
        selection.selectedIds.forEach((id) => {
          selected.add(String(id))
        })
        setSelectedIds(selected)
      }
    },
    [rejectedTransfers]
  )

  const handleSubmit = useCallback(async () => {
    const transferIds = Array.from(selectedIds)
    const result = await retryPayment({
      paymentId,
      employeeId: payerId,
      redirectUrl: window.location.href,
      transferIds: transferIds.length > 0 ? transferIds : undefined,
    })

    // Check for redirect URLs first (consent or authorization)
    if (result.consentUrl) {
      window.location.href = result.consentUrl
      return
    }

    if (result.authorizationUrl) {
      window.location.href = result.authorizationUrl
      return
    }

    // If no redirects needed and successful, close modal
    if (result.success) {
      onSuccess()
      onClose()
    }
  }, [selectedIds, paymentId, payerId, retryPayment, onSuccess, onClose])

  const isLoading = loadingTransfers || loadingRetry
  const hasSelection = selectedIds.size > 0
  const allSelected = selectedIds.size === rejectedTransfers.length && rejectedTransfers.length > 0

  // Button label: simple "Retry" when all selected, show count when some are deselected
  const buttonLabel = allSelected
    ? i18n.t('banking.bulkPayments.detail.retryModal.retry')
    : i18n.t('banking.bulkPayments.detail.retryModal.retryCount', { count: selectedIds.size })

  // Build defaultSelectedItems with all items checked based on current data
  const defaultSelectedItems = useMemo(() => {
    const items = new Map<string, { id: string; checked: boolean }>()
    rejectedTransfers.forEach((t) => {
      items.set(String(t.id), { id: String(t.id), checked: true })
    })
    return {
      allSelected: true as const,
      items,
      groups: new Map(),
    }
  }, [rejectedTransfers])

  // Data collection source with selectable and defaultSelectedItems
  const source = useDataCollectionSource(
    {
      dataAdapter: {
        fetchData: async () => ({
          records: rejectedTransfers,
        }),
      },
      selectable: (item: RejectedTransfer) => String(item.id),
      defaultSelectedItems,
    },
    [rejectedTransfers, defaultSelectedItems]
  )

  // Table columns
  const columns = useMemo(
    () => [
      {
        label: i18n.t('banking.bulkPayments.detail.retryModal.columns.beneficiary'),
        render: (item: RejectedTransfer) => ({
          type: 'text' as const,
          value: item.beneficiaryName ?? '-',
        }),
      },
      {
        label: i18n.t('banking.bulkPayments.detail.retryModal.columns.account'),
        render: (item: RejectedTransfer) => ({
          type: 'text' as const,
          value: item.accountNumber ?? '-',
        }),
      },
      {
        label: i18n.t('banking.bulkPayments.detail.retryModal.columns.amount'),
        render: (item: RejectedTransfer) => ({
          type: 'text' as const,
          value: MoneyWithCurrency({
            amount: item.amountCents,
            currency: item.currency,
            locale,
          }),
        }),
      },
      {
        label: i18n.t('banking.bulkPayments.detail.retryModal.columns.paymentRequest'),
        render: (item: RejectedTransfer) => ({
          type: 'text' as const,
          value: item.paymentRequestLabel ?? '-',
        }),
      },
      {
        label: i18n.t('banking.bulkPayments.detail.retryModal.columns.rejectionReason'),
        render: (item: RejectedTransfer) => ({
          type: 'status' as const,
          value: {
            label: translateRejectionReason(item.rejectionReason),
            status: 'critical' as const,
          },
        }),
      },
    ],
    [locale]
  )

  const visualizations = useMemo(
    () => [
      {
        type: 'table' as const,
        options: {
          columns,
        },
      },
    ],
    [columns]
  )

  return (
    <F0Dialog
      isOpen
      onClose={onClose}
      title={i18n.t('banking.bulkPayments.detail.retryModal.title')}
      width="lg"
      primaryAction={{
        label: buttonLabel,
        onClick: handleSubmit,
        disabled: !hasSelection || isLoading,
        loading: isLoading,
      }}
      secondaryAction={{
        label: i18n.t('banking.bulkPayments.detail.retryModal.cancel'),
        onClick: onClose,
        disabled: isLoading,
      }}
      loading={loadingTransfers}
    >
      <Box flexDirection="column" gap="s16">
        <F0Alert
          variant="info"
          title={i18n.t('banking.bulkPayments.detail.retryModal.infoTitle')}
          description={i18n.t('banking.bulkPayments.detail.retryModal.infoNote')}
        />

        {!loadingTransfers && rejectedTransfers.length > 0 && (
          <Box style={{ maxHeight: '400px', overflow: 'auto' }}>
            <OneDataCollection
              source={source}
              visualizations={visualizations}
              onSelectItems={handleSelectItems}
            />
          </Box>
        )}
      </Box>
    </F0Dialog>
  )
}
